import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {type IConversation, ReadStatus} from 'src/api/landlord-messages-api';
import {
    EMessageChannel,
    isMessageChannel,
    useLead,
    useLoggedInUser,
    useLeadMessages,
    useSendMessage,
    useShowings,
    useUpdateConversationsReadStatus,
    useFacebookIntegration,
    useProperty,
    useTeamSettings,
    useSnippets,
    useLeadStageHistory,
} from 'src/services/api';
import { LeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/context';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import { useUserSettings } from 'src/services/api/users';
import { LeadUtils } from 'src/utils/LeadUtils';
import { searchSnippets } from 'src/pages/landlord/leads/utils/search-snippets';
import {usePersistentState} from 'src/services/storage/use-persistent-state';

const basePath = '/landlord/inner/messages/from-lead';

const getConversationPath = (channel?: EMessageChannel, leadUuid?: string) => {
    if (!leadUuid || !channel) {
        return;
    }

    return `${basePath}?lead_uuid=${leadUuid}&channel=${channel}&show_navigation=true`;
};

const REFETCH_INTERVAL = 3000;

type Props = {
    children: React.ReactNode;
};

export default function LeadMessagesPageProvider({ children }: Props) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const leadUuid = searchParams.get('lead_uuid') as string;
    const channel = searchParams.get('channel') as EMessageChannel;
    const showNavigation = Boolean(searchParams.get('show_navigation'));

    const { data: facebookIntegration } = useFacebookIntegration();

    const setChannel = (newChannel: EMessageChannel) => {
        setSearchParams(prev => {
            const newSearchParams = new URLSearchParams(prev);
            newSearchParams.set('channel', newChannel);
            return newSearchParams.toString();
        });
    };

    if (!leadUuid) {
        console.error('Lead UUID must be set in URL params');

        return (
            <Alert severity="warning">
                Lead UUID must be set
            </Alert>
        );
    }

    if (channel && !isMessageChannel(channel)) {
        console.error(`Invalid send channel: ${channel}`);

        return (
            <Alert severity="warning">
                Invalid message channel: {channel || 'Channel not set'}.
                It must be either {EMessageChannel.SMS}, {EMessageChannel.EMAIL} or {EMessageChannel.FACEBOOK_MESSENGER}.
            </Alert>
        );
    }

    const { data: loggedInUser } = useLoggedInUser();
    const { data: userSettings } = useUserSettings();
    const { data: teamSettings } = useTeamSettings();
    const { data: snippets } = useSnippets();
    const { data: showings } = useShowings({ whole_team: true });
    const { data: lead } = useLead(leadUuid);
    const { data: property } = useProperty(lead?.property_id);
    const { data: messagesResponse, refetch: refetchMessages } = useLeadMessages(
        leadUuid,
        { refetchInterval: REFETCH_INTERVAL, include_attachments: true }
    );
    const { data: stages } = useLeadStageHistory(leadUuid);
    const messages = messagesResponse?.messages;
    const attachments = messagesResponse?.attachments;

    const { mutateAsync: updateReadStatus } = useUpdateConversationsReadStatus();

    const { mutateAsync: sendMessage, isLoading: isSendingMessage } = useSendMessage();

    const [ replyInput, setReplyInput ] = useState('');
    const [ isSnippetsModalOpen, setIsSnippetsModalOpen ] = useState(false);

    // Search state for snippets
    const [search, setSearch] = useState('');

    const [conversations, saveConversationsToLocalStorage] = usePersistentState(
        'conversations',
        [] as IConversation[],
        (value): value is IConversation[] => Array.isArray(value)
    );

    // Reset the conversations in local storage if the `show_navigation` param is not present
    useEffect(() => {
        if (!showNavigation) {
            saveConversationsToLocalStorage([]);
        }
    }, [showNavigation]);

    const filteredSnippets = useMemo(() => {
        const _filteredSnippets = snippets || [];
        if (!search) {
            return _filteredSnippets;
        }

        return searchSnippets(_filteredSnippets, search);
    }, [snippets, search]);

    const currentConversationIndex = conversations.findIndex((o) => o.lead_uuid === leadUuid);
    const previousConversation = conversations[currentConversationIndex - 1];
    const nextConversation = conversations[currentConversationIndex + 1];
    const conversationAfterNextConversation = conversations[currentConversationIndex + 2];

    // Prefetch the previous and next lead's messages
    useLead(previousConversation?.lead_uuid);
    useLeadMessages(previousConversation?.lead_uuid, { include_attachments: true });
    useProperty(previousConversation?.property_id);
    useLead(nextConversation?.lead_uuid);
    useLeadMessages(nextConversation?.lead_uuid, { include_attachments: true });
    useProperty(nextConversation?.property_id);
    useLead(conversationAfterNextConversation?.lead_uuid);
    useLeadMessages(conversationAfterNextConversation?.lead_uuid, { include_attachments: true });
    useProperty(conversationAfterNextConversation?.property_id);

    const previousLead = useLead(previousConversation?.lead_uuid)?.data;
    const nextLead = useLead(nextConversation?.lead_uuid)?.data;

    const previousConversationPath = getConversationPath(previousConversation?.latest_message_channel, previousLead?.uuid);
    const nextConversationPath = getConversationPath(nextConversation?.latest_message_channel, nextLead?.uuid);

    const navigateToPreviousConversation = () => {
        if (previousConversationPath) {
            navigate(previousConversationPath);
        }
    };

    const navigateToNextConversation = () => {
        if (nextConversationPath) {
            navigate(nextConversationPath);
        }
    };

    useEffect(() => {
        updateReadStatus({
            leadUuids: [leadUuid],
            status: ReadStatus.READ,
        });
    }, [leadUuid, messages]);

    useEffect(() => {
        if (!channel && lead && messages) {
            setChannel(LeadUtils.getDefaultMessageChannel(lead, messages));
        }
    }, [channel, lead, messages]);

    useEffect(() => {
        if (!channel && lead && messages) {
            setChannel(LeadUtils.getDefaultMessageChannel(lead, messages));
        }
    }, [channel, lead, messages]);

    const isLoading = !loggedInUser || !userSettings || !teamSettings || !snippets || (lead?.property_id && !property) || !showings || !lead || !messages || !stages;
    if (isLoading) {
        return (
            <LinearProgress />
        );
    }

    const handleSendMessage = async (message: string, files?: File[]) => {
        await sendMessage({
            leadUuid,
            channel,
            message,
            attachments: files,
        });
        await refetchMessages();
    };

    return (
        <LeadMessagesPageContext.Provider value={{
            loggedInUser,
            userSettings,
            teamSettings,
            showings,
            snippets: filteredSnippets,
            stages,
            teamMembers: loggedInUser.leasing_team_members || [],
            isFacebookConnected: !!facebookIntegration?.user_integration,

            lead,
            property,
            messages,
            attachments,

            channel,
            setChannel,
            showNavigation,

            conversations,
            previousLead,
            navigateToPreviousConversation,
            nextLead,
            navigateToNextConversation,

            replyInput,
            setReplyInput,

            sendMessage: handleSendMessage,
            isSendingMessage,

            isSnippetsModalOpen,
            setIsSnippetsModalOpen,

            search,
            setSearch,
        }}
        >
            {children}
        </LeadMessagesPageContext.Provider>
    );
}
